import storage, { StorageKeys } from "@/services/storage.service";
import { ILoginResponse } from "@/interfaces/_shared/auth";
import AuthModule from "@/store/modules/auth";
import { loginWithSmtzToken } from "@/api/auth/auth";
import { LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { NetworkErrors } from "@/constants/errors/network";
import { SharedCallbacks } from "../shared-resources";
import { ModalStatus } from "@/constants/modal";
import { redirectToLoginPage } from "./router.helper";
import i18n from "@/lang";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";

// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on i18n.global.t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import UserModule from "@/store/modules/user";
import router from "@/router";
import { getUser } from "@/api/user/user";
import axios from "axios";
import { Compliance } from "@smeetz/compliance-common";
import { IUser } from "@/interfaces/responses/user";
import { isBelgianCompliant } from "../vue-helpers/compliance";

// Returns access token
export function getUserAccessToken() {
  return storage.getItem(StorageKeys.AuthAccessToken);
}

export function getCognitoUserAccessToken() {
  return storage.getItem(StorageKeys.AuthAccessTokenB2B);
}

export function getSmtzAuthFromStorage() {
  return storage.getItem(StorageKeys.SmtzAuthToken);
}

const SMTZ_AUTH_REFRESH_WINDOW_MS = 15 * 60 * 1000;

// Reads the `exp` claim (ms since epoch) from a JWT or null
function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Seconds left before a JWT expires, or null when there is no token or no `exp` claim.
 * Logged instead of the token itself so session-timing issues are diagnosable from Graylog
 * without ever putting a credential in a log line.
 */
export function getTokenSecondsToExpiry(token?: string | null): number | null {
  if (!token) {
    return null;
  }
  const expiryMs = getTokenExpiryMs(token);
  return expiryMs === null ? null : Math.round((expiryMs - Date.now()) / 1000);
}

// True while the cached gql token is valid for longer than the refresh window.
function isSmtzAuthValid(token: string): boolean {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) {
    return true;
  }
  return expiryMs - Date.now() > SMTZ_AUTH_REFRESH_WINDOW_MS;
}

// Single source of truth for the GraphQL (smtz-auth) token.
// Returns the cached token while it is still valid
// and refresh it when it is missing or about to expire
export async function getSmtzAuth(): Promise<string> {
  const cached = getSmtzAuthFromStorage();
  if (cached && isSmtzAuthValid(cached)) {
    return cached;
  }
  const accessToken = getUserAccessToken();
  if (!accessToken) {
    // No session at all
    throw new Error(NetworkErrors.NoAccessToken);
  }
  try {
    const response = await loginWithSmtzToken(accessToken);
    const resAuthToken = response.token;
    setSmtzAuth(resAuthToken);
    return resAuthToken;
  } catch (error) {
    throw new Error(NetworkErrors.NoSmtzAuthToken);
  }
}

// Returns refresh token
export function getUserRefreshToken() {
  return storage.getItem(StorageKeys.AuthRefreshToken);
}

// Returns the expiration time for an access token.
export function getExpiresData() {
  return storage.getItem(StorageKeys.AuthExpiresIn);
}

export function getCognitoExpiresData() {
  return storage.getItem(StorageKeys.AuthExpiresInB2B);
}

export function setUserToken(token: string) {
  return storage.setItem(StorageKeys.AuthAccessToken, token);
}

export function setSmtzAuth(token: string) {
  return storage.setItem(StorageKeys.SmtzAuthToken, token);
}

export function setUserTokens(tokenObj: ILoginResponse) {
  LogService.info("Setting user tokens: ", { tokenObj });

  const expiresInDays = tokenObj.expires_in / (3600 * 24); // 5 days
  // set new expiry date
  const expiresAt = new Date(Date.now() + tokenObj.expires_in * 1000);

  const opts = {
    expires: expiresInDays,
  };

  storage.setItem(StorageKeys.AuthAccessToken, tokenObj.access_token, opts);
  storage.setItem(StorageKeys.AuthRefreshToken, tokenObj.refresh_token);
  storage.setItem(StorageKeys.AuthExpiresIn, expiresAt);

  // set order management tokens
  setOrderManagementTokens(".smeetz.com");
}

/**
 * Sets pos tokens into localStorage with a different domain to be used by b2b app
 * @param domain
 */
export function setOrderManagementTokens(domain: string) {
  const accessToken = getUserAccessToken() || "";
  const refreshToken = getUserRefreshToken() || "";
  const expiryToken = getExpiresData() || "";
  const opts = {};
  LogService.info(`Setting POS tokens values for B2B`, {
    domain,
    accessToken,
    refreshToken,
    expiryToken,
  });
  storage.setItem(StorageKeys.AuthAccessTokenB2B, accessToken, {
    ...opts,
    sameSite: "None",
    secure: true,
    domain: domain,
  });
  storage.setItem(StorageKeys.AuthRefreshTokenB2B, refreshToken, {
    sameSite: "None",
    secure: true,
    domain: domain,
  });
  storage.setItem(StorageKeys.AuthExpiresInB2B, expiryToken, {
    sameSite: "None",
    secure: true,
    domain: domain,
  });
}

export function removeUserTokens() {
  LogService.info("Removing user tokens");
  storage.clearTokens();
}

/**
 * Logs the user out and explains why with a blocking popup.
 *
 * @param title popup title
 * @param message popup body
 * @param force show the popup even on the login page, and even if a session
 * expiry popup is already in flight. Used for reasons that are not a session
 * expiry, such as a missing or invalid social security number, which have to
 * surface during login itself.
 * @param signWorkOutEvent whether the logout should report a WORK_OUT fiscal
 * event. False when the user never got as far as WORK_IN.
 */
export async function showPopupWithMessageAndLogout(
  title: string,
  message: string,
  force = false,
  signWorkOutEvent = true
) {
  // A single expired token can reach this from several places at once
  // (axios response interceptor, Apollo authLink/errorLink and the occupancy-calendar preflight).
  // Running the guard before any side effect makes sure the destructive logout
  // and the WORK_OUT compliance event fire only once.
  if (
    !force &&
    (AuthModule.showingSessionExpiryPopup ||
      router.currentRoute.value.path === "/login")
  ) {
    return;
  }
  AuthModule.setShowingSessionExpiryPopup(true);

  // In a case a modal is opened, we should close it
  const ModalSetStatus = SharedCallbacks.ModalSetStatus;
  if (ModalSetStatus) {
    ModalSetStatus(ModalStatus.Hidden);
  }

  AuthModule.setAccessToken("");
  const UserLogOut = SharedCallbacks.UserLogOut;
  if (UserLogOut) {
    UserLogOut({ signWorkOutEvent });
  }

  try {
    await showConfirm({
      title,
      message,
      variant: "warning",
      confirmText: "OK",
      cancelText: null,
    });
  } finally {
    // Always clear the flag and redirect, even if the confirm promise rejects
    // (e.g. a programmatic close), so the guard can never wedge and suppress a
    // later genuine session-expiry popup.
    AuthModule.setShowingSessionExpiryPopup(false);
    redirectToLoginPage();
  }
}

/**
 * Used when user token expires or when an api call returns an Unauthorized response
 * in this case we display an error, remove user tokens from cookies and redirect to the login page
 */
export async function showSessionExpiredPopup() {
  return showPopupWithMessageAndLogout(
    t("auth.session-expired"),
    t("auth.session-expired-msg")
  );
}

/**
 * Belgian operators must have a valid social security number: it is reported to
 * the FDM as the operator's fiscal id on every event. A Belgian user without a
 * valid one is logged out rather than allowed to sell unsigned.
 *
 * @returns whether the user may proceed
 */
export function checkUserSocialSecurityNumber(
  user: IUser = UserModule.user
): boolean {
  if (!isBelgianCompliant.value) {
    return true;
  }
  if (!user.socialSecurityNumber) {
    LogService.info(
      "User from Belgium with no social security number, logging out"
    );
    showPopupWithMessageAndLogout(
      t("compliance.social-security-number-required"),
      t("compliance.social-security-number-required-msg"),
      true,
      // the user never reached WORK_IN, so there is nothing to work out of
      false
    );
    return false;
  }
  if (!Compliance.isValidSecurityNumber(user.socialSecurityNumber)) {
    LogService.info(
      "User from Belgium with invalid social security number, logging out"
    );
    showPopupWithMessageAndLogout(
      t("compliance.social-security-number-invalid"),
      t("compliance.social-security-number-invalid-msg"),
      true,
      false
    );
    return false;
  }
  return true;
}

export async function checkUserAccessToken() {
  const tokensSet = getUserAccessToken();
  let isUserValid = true;
  try {
    await getUser();
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      [401, 403].includes(error.response.status)
    ) {
      isUserValid = false;
    }
  }
  if (!tokensSet || !isUserValid) {
    showSessionExpiredPopup();
    return false;
  }
  return true;
}
